import { useIntl } from "react-intl";
import {
  ButtonLink,
  ButtonLinkType,
  OptDecision,
  PurchaseLink,
} from "../../../../common/api/decimal/methods/course.ts";
import useChangeContentDecision from "./useChangeContentDecision.tsx";
import { CourseMaterial, useCoursesLoaderData } from "./coursesLoader.ts";
import { useLayoutData } from "../layout/myCoursesLayoutLoader.ts";
import { optedOutWithoutBook, optedOutWithBook } from "./printStatusHelper.ts";
import useVirtualStore from "./useVirtualStore.tsx";
import GlobalNavCart from "../../../../common/icons/GlobalNavCart.tsx";
import { getReaderNavURL } from "../../../bookshelfLinks.ts";
import useMaterialOptDeadline from "./useMaterialOptDeadline.tsx";
import { isMaterialInStoreCart } from "./common/storeProductUtils.ts";
import useStoreProduct from "./useStoreProduct.tsx";

export interface Action extends BaseAction {
  ariaLabel: string;
}

interface BaseAction {
  key: string; // used for focus management between different actions with same key
  label: string;
  onClick?: () => void | Promise<void>;
  href?: string;
  to?: string;
  category:
    | "access"
    | "favoredPurchase"
    | "info"
    | "illFavoredPurchase"
    | "details";
  type: ButtonLinkType | OptDecision | null;
  meta?: "compare" | "mini-pdp" | "main-pdp";
  // todo reconsider meta in relation to lonestar changes
  icon?: JSX.Element;
}

const order = {
  access: 1,
  favoredPurchase: 2,
  info: 3,
  illFavoredPurchase: 4,
  details: 5,
};

// Methods that are promises will show loading dots when clicked
export interface Methods {
  comparePurchaseOptions: () => void;
  confirmOptOutDialog: () => void;
  confirmOptInPrintDialog: () => void;
  viewDetails: () => void;
  launchBook: (path: string) => Promise<void>;
  accessCodeDialog: (url: string) => void;
  lmsLinkDialog: (url: string) => void;
}

export default function useMaterialActions({
  courseMaterial,
  methods,
}: {
  courseMaterial: CourseMaterial;
  methods: Methods;
}) {
  const actions: Action[] = [];
  const { buttonLinks, purchaseLinks, printStatus } = courseMaterial;
  const { config } = useLayoutData();
  const intl = useIntl();
  const { contentOptIn } = useChangeContentDecision({
    courseMaterial,
  });
  const optDecisionKey = `opt_decision_${courseMaterial.id}`;
  const hasOptIn = purchaseLinks.some((link) => link.type === "opt_in");
  const { isVirtualStore } = useVirtualStore();
  const { pdpLink } = useStoreProduct(courseMaterial);
  const { currentTerm } = useCoursesLoaderData();
  const isEquitableAccess =
    currentTerm?.institutionProgram === "equitable_access";
  const { optWindowOpen } = useMaterialOptDeadline(courseMaterial);
  const { storeCart } = useLayoutData();
  const isInStoreCart = isMaterialInStoreCart(
    courseMaterial,
    storeCart?.lineItems
  );

  const actionSort = () => {
    actions.sort(
      (a: BaseAction, b: BaseAction) => order[a.category] - order[b.category]
    );
    // view details should always be last unless it is the main action
    // other than ill favored purchase in which case it moves to top
    if (
      !actions.filter(
        (a) =>
          !(a.category === "illFavoredPurchase" || a.category === "details")
      ).length
    ) {
      const viewDetailsAction = actions.find((a) => a.category === "details");
      if (viewDetailsAction) {
        actions.splice(actions.indexOf(viewDetailsAction), 1);
        actions.unshift(viewDetailsAction);
      }
    }
  };

  const buildAriaLabel = (action: BaseAction) => {
    const description = `${action.label} - ${courseMaterial.title}`;
    const linkText = action.href
      ? intl.formatMessage({ defaultMessage: "(opens in a new window)" })
      : "";
    return `${description} ${linkText}`;
  };

  const pushAction = (action: BaseAction) => {
    actions.push({ ...action, ariaLabel: buildAriaLabel(action) });
  };

  const optInAction = (): BaseAction => ({
    key: optDecisionKey,
    label: intl.formatMessage({ defaultMessage: "Want to Opt In?" }),
    onClick: () => contentOptIn(),
    category: "favoredPurchase",
    type: "opt_in",
  });

  const optInPrintAction = (): BaseAction => {
    let label = null;
    if (optedOutWithoutBook(printStatus)) {
      label = intl.formatMessage({ defaultMessage: "Get Print Book" });
    } else if (optedOutWithBook(printStatus)) {
      label = intl.formatMessage({ defaultMessage: "Keep Print Book" });
    } else {
      return optInAction();
    }
    return {
      key: optDecisionKey,
      label,
      onClick: () => methods.confirmOptInPrintDialog(),
      category: "favoredPurchase",
      type: "opt_in",
    };
  };

  const optOutAction = (): BaseAction => ({
    key: optDecisionKey,
    label: intl.formatMessage({ defaultMessage: "Want to Opt Out?" }),
    onClick: () => methods.confirmOptOutDialog(),
    category: "illFavoredPurchase",
    type: "opt_out",
  });

  const viewDetails = (type: "panel" | "mainPDP" = "panel"): BaseAction => ({
    key: "view_details",
    label: intl.formatMessage({ defaultMessage: "View Details" }),
    onClick: type === "mainPDP" ? undefined : () => methods.viewDetails(),
    category: "details",
    to: type === "mainPDP" ? pdpLink : undefined,
    type: null,
  });

  const storeAddToCartAction = (): BaseAction => ({
    key: "store_add_to_cart",
    label: intl.formatMessage({ defaultMessage: "Add to Cart" }),
    category: "favoredPurchase",
    type: "ePOS",
    meta: "mini-pdp",
    // note action is non-trivial and handled in the mini-pdp
  });

  const storePurchaseOptionsAction = (): BaseAction => ({
    key: "view_store_purchase_options",
    label: intl.formatMessage({
      defaultMessage: "View More Purchase Options",
    }),
    to: pdpLink,
    category: "illFavoredPurchase",
    type: "ePOS",
    meta: "main-pdp",
  });

  const storeViewInCartAction = (): BaseAction => ({
    key: "store_view_in_cart",
    label: intl.formatMessage({
      defaultMessage: "View in Cart",
    }),
    category: "info",
    type: "ePOS",
    icon: <GlobalNavCart />,
    to: "/my-cart",
  });

  const getEPOSAction = (buttonLink: ButtonLink): BaseAction | null => {
    // epos is handled differently in virtual store compared to original FA
    if (isVirtualStore) {
      if (!isInStoreCart) {
        if (
          courseMaterial.materialStatus === "opted_out" &&
          !isEquitableAccess &&
          optWindowOpen
        ) {
          // special case highlights opt in but offers main pdp
          // but not when material is not present in the store
          return courseMaterial.product ? storePurchaseOptionsAction() : null;
        } else {
          return storeAddToCartAction();
        }
      } else {
        return storeViewInCartAction();
      }
    } else {
      return {
        key: buttonLink.type,
        label: intl.formatMessage({ defaultMessage: "Purchase Options" }),
        onClick: () => methods.comparePurchaseOptions(),
        category: hasOptIn ? "illFavoredPurchase" : "favoredPurchase",
        type: buttonLink.type,
        meta: "compare",
      };
    }
  };

  const getButtonLinkAction = (buttonLink: ButtonLink): BaseAction | null => {
    switch (buttonLink.type) {
      case "buy":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Buy Now" }),
          href: buttonLink.link,
          category: "illFavoredPurchase",
          type: buttonLink.type,
        };
      case "ePOS":
        return getEPOSAction(buttonLink);
      case "launch":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Book" }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      case "launch_book":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Book" }),
          onClick: () => methods.launchBook(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "open_book":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open Book" }),
          href: getReaderNavURL(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "launch_bundle_courseware":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      case "lms_link":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          onClick: () => methods.lmsLinkDialog(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "launch_bundle_reveal_code":
      case "launch_reveal_code":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Access Instructions" }),
          onClick: () => methods.accessCodeDialog(buttonLink.link),
          category: "access",
          type: buttonLink.type,
        };
      case "external_link":
      case "launch_access_code":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({ defaultMessage: "Open" }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      case "update_shipping":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({
            defaultMessage: "Update Shipping Address",
          }),
          href: buttonLink.link,
          category: "info",
          type: buttonLink.type,
        };
      case "view_shipping":
        return {
          key: buttonLink.type,
          label: intl.formatMessage({
            defaultMessage: "Shipping and Tracking",
          }),
          href: buttonLink.link,
          category: "access",
          type: buttonLink.type,
        };
      default:
        return null;
    }
  };

  buttonLinks.forEach((buttonLink: ButtonLink) => {
    const action = getButtonLinkAction(buttonLink);
    if (action) {
      pushAction(action);
    }
  });

  purchaseLinks.forEach((purchaseLink: PurchaseLink) => {
    if (purchaseLink.type === "opt_in") {
      if (
        config.assets.showPrintPickup &&
        courseMaterial.materialFormat === "print"
      ) {
        pushAction(optInPrintAction());
      } else {
        pushAction(optInAction());
      }
    } else if (purchaseLink.type === "opt_out") {
      pushAction(optOutAction());
    }
  });

  if (isVirtualStore) {
    const mainPdpExists = actions.find((a) => a.meta == "main-pdp");
    const pricingDataAvailable =
      courseMaterial.buttonLinks.some((b) => b.type === "ePOS") &&
      !courseMaterial.product;
    const canViewMorePurchaseOptions =
      courseMaterial.materialStatus === "not_included" ||
      (courseMaterial.materialStatus === "opted_out" && !optWindowOpen);

    if (!mainPdpExists) {
      // every material has "view details" but not with "View More Purchase Options" (main pdp)
      if (canViewMorePurchaseOptions) {
        // normally view details opens the side panel but in some pre-purchase scenarios it goes to main pdp
        pushAction(viewDetails("mainPDP"));
      } else {
        // if pricing data is unavailable mainPDP link is withdrawn still don't want the "View Details" side panel
        if (!pricingDataAvailable) {
          pushAction(viewDetails());
        }
      }
    }
  } else {
    pushAction(viewDetails());
  }

  actionSort();
  return actions;
}
